
import { ReallySticky } from "@/global-scripts/ReallySticky.ts";
import { Component, Vue, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import PrintBasketModule from "@/store/modules/PrintBasketModule";
import { PrintGroup } from "@/models/PrintGroup";
import FilterModule from "@/store/modules/FilterModule";
import { Helpers } from "@/global-scripts/helpers";
import ContentModule from "@/store/modules/ContentModule";
import { PrintObject } from "@/models/PrintObject";
import SortableGroup from "@/components/PrintBasket/SortableGroup/SortableGroup.vue";
import SortableGroupItem from "@/components/PrintBasket/SortableGroup/SortableGroupItem.vue";

import $ from "jquery";

@Component({
  components: {
    SortableGroup,
    SortableGroupItem,
  },
})
export default class PrintBasket extends Vue {
  private readonly printBasketModule = getModule(PrintBasketModule, this.$store);
  private readonly filterModule = getModule(FilterModule, this.$store);
  private readonly contentModule = getModule(ContentModule, this.$store);

  private reallySticky!: ReallySticky;

  private confirmClear = false;

  private _printList: PrintGroup[];

  private get ready(): boolean {
    return this.contentModule.allDataLoaded && this.filterModule.allFiltersSet;
  }

  private get locale(): string {
    return this.contentModule.locale;
  }
  private get printBasketOpen(): boolean {
    return this.printBasketModule.printBasketOpen;
  }

  private get componentClass(): string {
    const classes = [];

    if (this.count === 0) {
      classes.push("empty");
    }
    if (this.printBasketOpen) {
      classes.push("is-opened");
    }

    if (this.count >= this.$config.topicBasketLimit) {
      classes.push("limit-reached");
    } else if (this.count / this.$config.topicBasketLimit >= 0.8) {
      classes.push("limit-warning");
    }

    return classes.join(" ");
  }

  private get limitMessage(): string {
    if (this.count >= this.$config.topicBasketLimit) {
      return this.$t.PrintBasketLimitReached;
    } else if (this.count / this.$config.topicBasketLimit >= 0.8) {
      return this.$t.PrintBasketLimitWarning;
    }

    return "";
  }

  private get count(): number {
    return this.printBasketModule.printBasketCount;
  }

  private get printList(): PrintGroup[] {
    return this.printBasketModule.groupedPrintObjects;
  }

  private set printList(val: PrintGroup[]) {
    // We always store the basket as a flat list and not grouped by category
    const flatList: PrintObject[] = [];
    val.forEach((group) => {
      group.topics.forEach((topic) => {
        flatList.push(topic);
      });
    });
    
    this.setPrintBasket(flatList);
  }

  private setPrintBasket(topics: PrintObject[]) {
    const printBasket = this.printBasketModule.printBasket;
    printBasket.printObjects = topics;
    this.printBasketModule.setPrintBasket(printBasket);
    this.printBasketModule.updatePrintBasket(topics);
  }

  private closePrintBasket() {
    this.printBasketModule.setPrintBasketOpen(false);
  }

  private removeAll() {
    if (this.confirmClear) {
      this.printList = [];
    }

    this.$tracker.trackPrintBasketEvent(`clear all`, window.location.href);

    this.confirmClear = !this.confirmClear;
  }

  private goToPreview() {
    this.printBasketModule.setPrintBasketOpen(false);
    // /${activeLocale}/print
    this.$router.push({ path: `${this.filterModule.basePath}/export` });
  }

  initKeyEvents() {
    $("body")
      .on("keydown", (e) => {
        if (this.contentModule.feedbackFormOpen) return; // Feedback form should get all events
      })
      .on("keyup", (e) => {
        // ESCAPE KEY (can only be detected in keyup event)
        if (e.keyCode === 27) {
          this.closePrintBasket();
        }
      });
  }

  @Watch("printBasketOpen")
  private onPrintBasketOpenChange(newVal: boolean) {
    window.requestAnimationFrame(() => {
      if (newVal) {
        $("body").addClass("print-basket-open");
        Helpers.setScrollBarOffset();
      } else {
        setTimeout(() => {
          $("body").removeClass("print-basket-open");
          Helpers.removeScrollBarOffset();
        }, 320); // wait until transition completed
      }
    });
  }

  destroyed() {
    $("body").off("keydown").off("keyup");
  }

  created() {
    this.$nextTick(() => {
      this.reallySticky = new ReallySticky($(".PrintBasket-inner"), {
        containerSel: ".page > .main-wrapper",
        fixedTopNavSel: "#submenu",
        offset: 50,
        breakpoint: 992,
      });

      $(window).on("scroll", () => {
        this.reallySticky.update();
      });
      $(window).on("resize", () => {
        this.reallySticky.update();
      });
    });

    this.initKeyEvents();
  }
}
